import { AfterViewInit, Component, ElementRef, HostBinding, Input, OnChanges, OnDestroy, OnInit, Renderer2, SimpleChanges, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';

@Component({
  selector: 'svc-drawer',
  templateUrl: './svc-drawer.component.html',
  styleUrls: ['./svc-drawer.component.scss'],
})
export class SvcDrawerComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() position: 'left' | 'right' = 'right';
  @Input() mode: 'over' | 'side' = 'side';
  @Input() opened = false;

  @HostBinding('class.positioned-left') public isLeft = false;
  @HostBinding('class.positioned-right') public isRight = false;
  @HostBinding('class.over') public isOver = this.mode === 'over';
  @HostBinding('class.opened') public isOpened = this.opened;

  @ViewChild('drawerSide') protected drawerSideRef: ElementRef<HTMLElement>;
  @ViewChild('drawerContent') protected drawerContentRef: ElementRef<HTMLElement>;

  public drawerSideEl: HTMLElement;
  public drawerContentEl: HTMLElement;

  private _drawerSideHiddenTimeout: ReturnType<typeof setTimeout> | null = null;
  private _viewInit = false;
  private _isOpened = new Subject<boolean>();
  public isOpened$ = this._isOpened.asObservable();
  private _destroy = new Subject<void>();
  public destroy$ = this._destroy.asObservable();

  constructor(private renderer: Renderer2) { }

  public ngOnInit(): void { }

  public ngAfterViewInit(): void {
    this._viewInit = true;
    this._updateVariables();
    this._updateViews();
    this.drawerSideEl = this.drawerSideRef.nativeElement;
    this.drawerContentEl = this.drawerContentRef.nativeElement;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this._updateVariables();
    this._updateViews();

    if ('opened' in changes && this._viewInit && changes.opened.currentValue !== changes.opened.previousValue) {
      this._isOpened.next(this.opened);
    }
  }

  private _updateVariables(): void {
    if (this._viewInit) {
      this.isLeft = this.position == 'left';
      this.isRight = this.position == 'right';
    }
    this.isOver = this.mode == 'over';
    this.isOpened = this.opened;
  }

  private _updateViews(): void {
    if (this._viewInit && this.drawerContentRef && this.drawerSideRef) {
      this.renderer.setStyle(this.drawerContentRef.nativeElement, 'width', this.opened && !this.isOver ? `calc(100% - ${this.drawerSideRef.nativeElement.offsetWidth}px)` : null);

      this._drawerSideHiddenTimeout && clearTimeout(this._drawerSideHiddenTimeout);
      if (this.opened) {
        if (!this.drawerSideRef.nativeElement.style.visibility) {
          this.renderer.setStyle(this.drawerSideRef.nativeElement, 'visibility', 'visible');
        }
      } else {
        if ((this.drawerSideRef.nativeElement.style.visibility || '') !== '') {
          this._drawerSideHiddenTimeout = setTimeout(() => {
            this.renderer.setStyle(this.drawerSideRef.nativeElement, 'visibility', null);
          }, 300);
        }
      }

      const transform = this.opened ? `translateX(0)` : null;
      if (this.drawerSideRef.nativeElement.style.transform !== transform) {
        this.renderer.setStyle(this.drawerSideRef.nativeElement, 'transform', transform);
      }
    }
  }

  public toggle(forceState?: boolean): void {
    if (forceState === this.opened) return;
    this.opened = forceState ?? !this.opened;
    this._isOpened.next(this.opened);
    this._updateVariables();
    this._updateViews();
  }

  public ngOnDestroy(): void {
    this._destroy.next();
    this._destroy.complete();
  }
}
